<template>
	<div>
		<slot/>
	</div>
</template>
<script setup lang="ts">
import type {LatLng} from "@spoferan/spoferan-ts-core";
import {deepEqual, formatLatLng} from "@spoferan/spoferan-ts-core";
import {locationIcon} from "../../config";

import type {Ref} from 'vue';
import {inject, nextTick, onBeforeUnmount, onMounted, ref, watch} from 'vue';

import {mapInjection} from "../../types/keys";
import type MapMap from "./MapMap.client.vue";

const icons = {
	location: locationIcon,
};

const props = withDefaults(defineProps<{
	// The geolocation of the marker
	latLng?: [number, number] | LatLng,

	// The title to show when hovering over the marker
	title?: string,

	// Whether the element shall retrieve a non focused styling, i.e., a decreased opacity
	unfocused?: boolean,

	// Whether the marker can be dragged
	draggable?: boolean,

	// The opacity of the marker
	opacity?: number,

	// Whether the marker arrow at the bottom of the marker shall be hidden
	noArrow?: boolean,

	// The icon to show on the marker
	icon?: string,

	// The text to show on the marker
	text?: string | number,

	// The image to show on the marker
	image?: string,

	// The css color string to use as the background color of the marker
	color?: string,

	// The css color string to use as the border color of the marker
	borderColor?: string,

	// The width of the marker
	width?: number,

	// The height of the marker
	height?: number,

	// The z-index of the marker
	zIndex?: number,

	tile?: boolean,

	// Whether to show a pulsating shadow on the marker
	pulse?: boolean,
}>(), {
	color: '#008200',
	width: 40,
	height: 40,
});

const emit = defineEmits(['click', 'contextmenu', 'update:latLng']);

const map = inject<InstanceType<typeof MapMap>>(mapInjection);

const latLng: Ref<LatLng> = ref(formatLatLng(props.latLng));

let mapObject = null;

watch(() => props.latLng, (newValue) => {
	const newLatLng = formatLatLng(newValue);
	if (deepEqual(newLatLng, latLng.value)) {
		return;
	}

	mapObject.setLatLng(newLatLng);
	latLng.value = newLatLng;
});
watch(latLng, (newValue) => {
	if (deepEqual(newValue, formatLatLng(props.latLng))) {
		return;
	}
	emit('update:latLng', newValue);
});
watch(() => props.icon, () => {
	updateIcon();
});
watch(() => props.image, () => {
	updateIcon();
});
watch(() => props.text, () => {
	updateIcon();
});
watch(() => props.color, () => {
	updateIcon();
});
watch(() => props.borderColor, () => {
	updateIcon();
});
watch(() => props.unfocused, () => {
	updateIcon();
});
watch(() => props.opacity, () => {
	updateIcon();
});
watch(() => props.pulse, () => {
	updateIcon();
});


/**
 * Updates the marker icon using the component's props.
 */
async function updateIcon() {
	const {MarkerIcon} = await import('./MarkerIcon');

	const markerIcon = new MarkerIcon({
		pulse: props.pulse,
		color: props.color,
		borderColor: props.borderColor,
		width: props.width,
		height: props.height,
		tile: props.tile,
		noArrow: props.noArrow,
		styles: props.unfocused ? {opacity: 0.5} : (props.opacity ? {opacity: props.opacity} : null)
	});

	if (props.image) {
		mapObject.setIcon(markerIcon.getImageContent(props.image));
	} else if (props.text) {
		mapObject.setIcon(markerIcon.getTextContent(props.text));
	} else {
		mapObject.setIcon(markerIcon.getIconContent(props.icon ?? icons.location));
	}
}

onMounted(() => {
	nextTick(async () => {
		const {marker: leafletMarker} = await import('leaflet/dist/leaflet-src.esm');

		mapObject = leafletMarker(latLng.value, {
			title: props.title,
			opacity: props.opacity,
			riseOnHover: true,
			draggable: props.draggable,
		});
		await updateIcon();

		mapObject.on('click', (event) => {
			emit('click', formatLatLng(event.latlng))
		});
		mapObject.on('contextmenu', (event) => {
			emit('contextmenu', formatLatLng(event.latlng))
		});
		mapObject.on('move', (event) => {
			if (props.draggable) {
				latLng.value = formatLatLng(event.latlng)
			}
		});

		map.addElement(mapObject);

	});
});

onBeforeUnmount(() => {
	if (mapObject) {
		mapObject.remove();
	}
})

</script>
<style>
/*********************
  Inputs
**********************/
.leaflet-marker-icon {
  background: none !important;
  border: none !important;
}

.map-marker {
  display: flex;
  align-items: center;
  justify-content: center;
  height: inherit;
  width: inherit;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
}
.map-marker .map-marker-icon {
  height: 100%;
  width: 100%;
  z-index: 1;
  border-radius: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
}
.map-marker .map-marker-icon > img {
  border-radius: inherit;
}
.map-marker .map-marker-icon > i {
  color: #ffffff;
  font-size: 155%;
}
.map-marker .map-marker-icon > svg {
  color: #ffffff;
  height: 60%;
  width: 60%;
}
.map-marker.map-marker-white {
  background: #ffffff;
}
.map-marker.map-marker-black {
  background: #2e3535;
}
.map-marker.map-marker-primary {
  background: #008200;
}
.map-marker.map-marker-secondary {
  background: #f0820a;
}
.map-marker.map-marker-error {
  background: #FA5555;
}
.map-marker.map-marker-success {
  background: #47a447;
}
.map-marker.map-marker-warning {
  background: #EB9E05;
}
.map-marker.map-marker-transparent {
  background: transparent;
}
.map-marker.map-marker-gray {
  background: #465050;
}
.map-marker.map-marker-gray-100 {
  background: #FBFBFB;
}
.map-marker.map-marker-gray-150 {
  background: #f4f4f5;
}
.map-marker.map-marker-gray-175 {
  background: #ececee;
}
.map-marker.map-marker-gray-200 {
  background: #d8d8d8;
}
.map-marker.map-marker-gray-300 {
  background: #bebebe;
}
.map-marker.map-marker-gray-400 {
  background: #7D8484;
}
.map-marker.map-marker-gray-450 {
  background: #5e6b6b;
}
.map-marker.map-marker-gray-500 {
  background: #465050;
}
.map-marker.map-marker-gray-600 {
  background: #2e3535;
}
.map-marker.map-marker-heading-color {
  background: #2e3535;
}
.map-marker.map-marker-text-color {
  background: #465050;
}
.map-marker.map-marker-subtext-color {
  background: #5e6b6b;
}
</style>
